/**
 * Stamps the active tenant's identifiers onto every span, so it does not
 * have to be threaded through every call site that starts one.
 *
 * With query strings dropped and URL path segments normalized
 * (privacy-span-processor.ts), a URL is no longer a reliable way to
 * correlate a trace back to a tenant — and never was, for a host-addressed
 * deployment, since the tenant there lives in the `Host` header, not the
 * path, and never reaches the browser's own URL at all. A dedicated
 * attribute set here is the one mechanism that works the same way
 * regardless of addressing mode.
 *
 * Tenant identifiers are business/organizational data, not personal data —
 * see the scope note in privacy-span-processor.ts — so none of this is
 * sanitized or gated the way `enduser.id` and email/GUID content are.
 *
 * `setTelemetryTenant` is wired from `PlatformSessionProvider`
 * (src/platform/session-context.tsx), which already resolves the session's
 * `Tenant` record — including `entraTenantId`, once bound — as part of its
 * normal `getCurrentSession()` call. No separate MSAL lookup is needed: the
 * platform's own tenant record is the single source for both ids, and it is
 * populated the same way whether the caller authenticated via a bearer
 * token or a host-only session cookie.
 *
 * @module telemetry/tenant-context-span-processor
 */
import type { Span, SpanProcessor } from "@opentelemetry/sdk-trace-web";

export interface TelemetryTenant {
  /** SocratIQ platform tenant id, e.g. `tenant-socratiq`. */
  id: string;
  /** SocratIQ platform tenant slug, e.g. `socratiq`, `chevron`. Absent until one is assigned. */
  slug?: string;
  /** Entra (Azure AD) directory tenant id this tenant's people sign in from. Absent until bound. */
  entraTenantId?: string;
}

let currentTenant: TelemetryTenant | undefined;

/** Set the tenant stamped onto every span started from now on. `undefined` clears it. */
export function setTelemetryTenant(tenant: TelemetryTenant | undefined): void {
  currentTenant = tenant;
}

/** For tests: read back the currently configured tenant. */
export function getTelemetryTenant(): TelemetryTenant | undefined {
  return currentTenant;
}

/**
 * Stamps `socratiq.tenant.id` / `socratiq.tenant.slug` /
 * `socratiq.tenant.entra_tenant_id` on every span at start, from whatever
 * `setTelemetryTenant` last set. Namespaced under `socratiq.`: there is no
 * stable OTel semantic convention for multi-tenancy yet, and these are
 * specific to this application rather than a generic signal.
 */
export class TenantContextSpanProcessor implements SpanProcessor {
  onStart(span: Span): void {
    if (!currentTenant) {
      return;
    }
    span.setAttribute("socratiq.tenant.id", currentTenant.id);
    if (currentTenant.slug) {
      span.setAttribute("socratiq.tenant.slug", currentTenant.slug);
    }
    if (currentTenant.entraTenantId) {
      span.setAttribute("socratiq.tenant.entra_tenant_id", currentTenant.entraTenantId);
    }
  }

  onEnd(): void {}

  shutdown(): Promise<void> {
    return Promise.resolve();
  }

  forceFlush(): Promise<void> {
    return Promise.resolve();
  }
}
